import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, Drawer, TextField } from "@outpave/ds";
import { Box, Button, Typography } from "@mui/material";
import { ThemeColors } from "utils/themeContextHelper";
import { Stack, Inline } from "components/layout/reusableLayouts";
import {
  BRAND_COLOR_KEYS,
  BRAND_KNOB_LABELS,
} from "components/common/branding/BrandColorsPanel";

export interface SavedScheme {
  id: number;
  name: string;
  colors: ThemeColors;
}

const STORAGE_KEY = "colorSchemes";

const readSchemes = (): SavedScheme[] => {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || "[]",
    );
    return Array.isArray(parsed) ? (parsed as SavedScheme[]) : [];
  } catch {
    return [];
  }
};

/**
 * A preset is identified by its name, which is what the drawer shows, what the
 * Save button requires and what the replace dialog says. It used to match on
 * three colours instead, so saving under a new name offered to replace an
 * unrelated preset and then renamed it.
 */
export const findSchemeByName = (
  schemes: SavedScheme[],
  name: string,
): SavedScheme | undefined => {
  const wanted = name.trim().toLowerCase();
  return schemes.find((scheme) => scheme.name.trim().toLowerCase() === wanted);
};

interface BrandPresetDrawerProps {
  open: boolean;
  handleCloseBrandPresetDrawer: () => void;
  onApplyScheme: (colors: ThemeColors) => void;
  currentColors: ThemeColors;
}

const BrandPresetDrawer = ({
  open,
  handleCloseBrandPresetDrawer,
  onApplyScheme,
  currentColors,
}: BrandPresetDrawerProps) => {
  const { t } = useTranslation();

  const [savedSchemes, setSavedSchemes] = useState<SavedScheme[]>([]);
  const [schemeName, setSchemeName] = useState("");
  const [duplicate, setDuplicate] = useState<SavedScheme | null>(null);

  useEffect(() => {
    setSavedSchemes(readSchemes());
  }, [open]);

  const saveScheme = (schemes: SavedScheme[], replaceId?: number) => {
    const name = schemeName.trim();
    const next = replaceId
      ? schemes.map((scheme) =>
          scheme.id === replaceId
            ? { ...scheme, colors: currentColors, name: name || scheme.name }
            : scheme,
        )
      : [...schemes, { id: Date.now(), name, colors: currentColors }];

    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSavedSchemes(next);
    setSchemeName("");
  };

  const handleSaveSchemeToLocal = () => {
    const schemes = readSchemes();
    const existing = findSchemeByName(schemes, schemeName);
    if (existing) {
      setDuplicate(existing);
      return;
    }
    saveScheme(schemes);
  };

  const handleDeleteScheme = (id: number) => {
    const next = savedSchemes.filter((scheme) => scheme.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSavedSchemes(next);
  };

  const handleSelectScheme = (colors: ThemeColors) => {
    onApplyScheme(colors);
    handleCloseBrandPresetDrawer();
  };

  const handleConfirmReplace = () => {
    if (duplicate) saveScheme(readSchemes(), duplicate.id);
    setDuplicate(null);
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleCloseBrandPresetDrawer}
        title={t("Brand Presets")}
        footer={
          <Button
            variant="primary-outlined"
            onClick={handleCloseBrandPresetDrawer}
          >
            {t("Close")}
          </Button>
        }
      >
        <Stack gap="var(--space-5)">
          <Inline gap="var(--space-4)" justify="between" align="end">
            <TextField
              label={t("Scheme Name")}
              value={schemeName}
              onChange={(e) => setSchemeName(e.target.value)}
              sx={{ width: 300 }}
            />
            <Button
              variant="primary-filled"
              onClick={handleSaveSchemeToLocal}
              disabled={!schemeName.trim()}
            >
              {t("Save Scheme")}
            </Button>
          </Inline>

          <Stack gap="var(--space-3)">
            <Typography variant="subtitle1">{t("Saved Schemes")}</Typography>

            {savedSchemes.map((scheme) => (
              <Stack key={scheme.id} gap="var(--space-2)">
                <Inline justify="between" align="center" wrap={false}>
                  <Button
                    variant="primary-outlined"
                    onClick={() => handleSelectScheme(scheme.colors)}
                  >
                    {scheme.name}
                  </Button>
                  <Button
                    size="small"
                    variant="danger-outlined"
                    onClick={() => handleDeleteScheme(scheme.id)}
                  >
                    {t("Delete")}
                  </Button>
                </Inline>

                {/* One swatch per knob the form carries, so a preset previews
                    everything it will apply. */}
                <Inline gap="var(--space-2)" wrap>
                  {BRAND_COLOR_KEYS.map((key) => {
                    const hex = scheme.colors[key as keyof ThemeColors];
                    return (
                      <Box
                        key={key}
                        title={t(BRAND_KNOB_LABELS[key])}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "var(--radius-pill)",
                          backgroundColor: hex || "var(--color-bg-elevated)",
                          border:
                            "var(--border-width-1) solid var(--color-border)",
                        }}
                      />
                    );
                  })}
                </Inline>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Drawer>

      <ConfirmDialog
        open={Boolean(duplicate)}
        tone="primary"
        title={t("Replace Scheme?")}
        message={t("ReplaceSchemeConfirm", { name: duplicate?.name ?? "" })}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={handleConfirmReplace}
        onCancel={() => setDuplicate(null)}
      />
    </>
  );
};

export default BrandPresetDrawer;
